import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ModuleEntitlementInterceptor } from 'src/app/core/interceptor/module-entitlement.interceptor';
import { EntitlementsService } from 'src/app/core/service/entitlements.service';
import { UpgradeModalService } from './upgrade-modal/upgrade-modal.service';

/**
 * SD-1229: turns "not on your plan" into an offer, wherever it happens.
 *
 * The API marks a refusal a plan would fix -- a feature some plan on sale includes, or a plan
 * limit reached -- and ModuleEntitlementInterceptor broadcasts it. This answers with the
 * upgrade modal, so no page has to handle the 403 itself. A refusal no plan on sale would fix
 * (a Pilot module, say) is left to the page, and the entitlement set refreshes either way so
 * a stale menu corrects itself.
 *
 * It also opens the modal for `?upgrade=<code>` on any page, so a link in a toast or an email
 * can offer the upgrade where the person already is instead of sending them to Settings.
 *
 * Started once by the authenticated shell (ContentLayoutComponent).
 */
@Injectable({ providedIn: 'root' })
export class PlanPromptService {
  private started = false;

  private static readonly Names: { [code: string]: string } = {
    'emails.inbox-automator': 'Inbox Automator',
    'documents-per-month': 'more documents',
    'seats': 'more users'
  };

  constructor(
    private upgradeModal: UpgradeModalService,
    private entitlements: EntitlementsService,
    private router: Router
  ) { }


  start(): void {
    if (this.started) { return; }
    this.started = true;

    ModuleEntitlementInterceptor.moduleNotEntitled$.subscribe(() => this.entitlements.refresh());

    this.openFromUrl();
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.openFromUrl());

    ModuleEntitlementInterceptor.upgradeSuggested$.subscribe(({ kind, code }) => {
      this.upgradeModal.open({
        reason: kind + ':' + code,
        requiredFeature: kind === 'module' ? code : undefined,
        featureName: PlanPromptService.Names[code]
      });
    });
  }

  private openFromUrl(): void {
    const tree = this.router.parseUrl(this.router.url);
    const code = tree.queryParams['upgrade'];
    if (!code) { return; }

    // Drop the parameter first, so a refresh or Back does not open the modal again.
    this.router.navigate([], { queryParams: { upgrade: null }, queryParamsHandling: 'merge', replaceUrl: true });

    const isFeature = code.indexOf('.') > 0;
    this.upgradeModal.open({
      reason: 'link:' + code,
      requiredFeature: isFeature ? code : undefined,
      featureName: PlanPromptService.Names[code]
    });
  }
}
